import React, { useState, useEffect, useMemo } from 'react';
import axios from "axios";
import Select from 'react-select';
import Swal from "sweetalert2";
import Parser from 'html-react-parser';
import { Link, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../api";
import Loader from "./Loader";  // Assuming you have a Loader component
import Skeleton from "react-loading-skeleton"; // Import Skeleton

const LedgerCompDue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        user_id: localStorage.getItem('userId'),
    };

    const fetchLedger = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/ledger/project-due`,
                { headers }
            );

            let dueData = response.data.filter(ledger => ledger.total_debit - ledger.total_credit > 0);
            setData(dueData); //only show incomplete tasks

        } catch (err) {
            console.log(err);
            setError('Error fetching ledger data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLedger();
    }, []);

    const toCamelCase = (str) => {
        const camelCased = str.replace(/_([a-z])/g, (match, group1) => group1.toUpperCase());
        return camelCased.charAt(0).toUpperCase() + camelCased.slice(1);
    };

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    useEffect(() => {
        setCurrentPage(1);
    }, [search, itemsPerPage]);

    const handleSort = (key) => {
        setSortConfig((prevConfig) => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
        }));
    };

    const getSortValue = (entry, key) => {
        switch (key) {
            case "client":
                return entry.client?.full_name || "";
            case "lastbill":
                return entry.lastbill?.title || "";
            case "debit":
                return Number(entry.total_debit) || 0;
            case "credit":
                return Number(entry.total_credit) || 0;
            case "due":
                return Number(entry.total_debit - entry.total_credit) || 0;
            default:
                return "";
        }
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <i className="fas fa-sort ms-1 text-muted"></i>;
        }
        return sortConfig.direction === "asc"
            ? <i className="fas fa-sort-up ms-1"></i>
            : <i className="fas fa-sort-down ms-1"></i>;
    };

    const filteredData = data.filter((ledger) =>
        [
            ledger.project.project_name,
            ledger.client.full_name,
            ledger.lastbill.firm,
            ledger.lastbill.date,
            ledger.lastbill.title,
            ledger.lastbill.start_date,
            ledger.lastbill.end_date,
            ledger.lastbill.debit,
            ledger.lastbill.credit,
            ledger.renewal_month,
            ledger.total_debit,
            ledger.total_credit,
        ]
            .filter(Boolean) // Prevent errors from null values
            .some((field) => field.toString().toLowerCase().includes(search))
    );

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortConfig.key) return 0;

        const valueA = getSortValue(a, sortConfig.key);
        const valueB = getSortValue(b, sortConfig.key);

        if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = sortedData.slice(startIndex, startIndex + parseInt(itemsPerPage));



    const renderLedgerTableTotal = () => {

        const totalCredit = filteredData.map((row) => row.total_credit).reduce((a, b) => a + b, 0);
        const totalDebit = filteredData.map((row) => row.total_debit).reduce((a, b) => a + b, 0);
        return (<tfoot><tr>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td><b>{formatINR(Math.round(parseFloat(totalDebit) - parseFloat(totalCredit)))}</b></td>
        </tr></tfoot>);
    }

    const formatINR = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 2,
        }).format(amount);
    };

    if (loading) {
        return <div> <p>Loading..</p> </div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="content">

            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                <div className="my-auto mb-2 text-start">
                    <div className="pagetitle">
                        <h1>Ledger</h1>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="#">Home</a>
                                </li>
                                <li className="breadcrumb-item active">Ledger</li>
                                <li className="breadcrumb-item active">Due</li>
                            </ol>
                        </nav>
                    </div>
                </div>
                <Link to={`/dashboard/add-ledger`} title="Add Ledger"
                    className="btn btn-primary d-flex align-items-center">
                    <i className="fa fa-plus-circle me-2"></i>New Entry
                </Link>
            </div>

            {/* Show Loader when loading */}
            {loading ? (

                <div className="skeleton-loader-table">
                    <table className="table table-bordered dataTable" id="datatable">
                        <thead>
                            <tr>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                                <th><div className="skeleton-loader"></div></th>
                            </tr>
                        </thead>
                    </table>
                </div>
            ) : (
                <>
                    <div className="card-body p-0">
                        <div className="table-responsive custom-datatable-filter">
                            <div className="row mt-2 justify-content-between mb-3">
                                <div className="d-flex justify-content-between align-items-center dt-layout-start col-md-auto me-auto">
                                    <label htmlFor="dt-length-0"> entries per page</label>
                                    <select aria-controls="datatable" className="form-select form-select-sm" id="dt-length-0" value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(e.target.value)}>
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                                <div className="d-md-flex justify-content-between align-items-center dt-layout-end col-md-auto ms-auto">
                                    <label htmlFor="dt-search-0" className="form-label me-2 visually-hidden">Search:</label>
                                    <input
                                        type="text"
                                        placeholder="Search..."
                                        className="p-2 border rounded mb-4 w-full"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <table className="table table-bordered dataTable" id="datatable">
                                <thead>
                                    <tr>
                                        <th>SR.No</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("client")}>Client {renderSortIcon("client")}</th>
                                        {/* <th>FIRM</th> */}
                                        {/* <th>Date</th> */}
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("lastbill")}>Last Bill {renderSortIcon("lastbill")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("debit")}>Debit {renderSortIcon("debit")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("credit")}>Credit {renderSortIcon("credit")}</th>
                                        <th style={{ cursor: "pointer" }} onClick={() => handleSort("due")}>Due {renderSortIcon("due")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedData.map((entry, index) => (
                                        (parseFloat(entry.total_debit) - parseFloat(entry.total_credit) != 0 ?
                                            <tr key={entry.id || index} id={`ledger${entry.id}`}>
                                                <td> {((currentPage - 1) * itemsPerPage) + (index + 1)}</td>
                                                <td>{Parser(entry.client != null ? entry.client.full_name : "")}{Parser(entry.project != null ? "<br>" + entry.project.project_name : "")}</td>
                                                {/* <td>{entry.lastbill.firm}</td> */}
                                                {/* <td>{entry.lastbill.date}</td> */}
                                                <td>{toCamelCase(entry.lastbill.title)}{Parser(entry.lastbill.start_date != null ? '<br>Start Date: ' + entry.lastbill.start_date : "")}{Parser(entry.lastbill.start_date != null ? '<br>End Date: ' + entry.lastbill.end_date : "")}{Parser(entry.lastbill.renewal_month != null ? "<br>Retainer : " + entry.lastbill.renewal_month + " month" : "")}</td>
                                                <td>{formatINR(entry.total_debit)}</td>
                                                <td>{formatINR(entry.total_credit)}</td>
                                                <td>{formatINR(parseFloat(entry.total_debit) - parseFloat(entry.total_credit))}</td>
                                            </tr>
                                            : null)
                                    ))}
                                </tbody>
                                {renderLedgerTableTotal()}
                            </table>
                            <div className="flex justify-center mt-4">
                                <button
                                    className="px-3 py-1 mx-1 border rounded disabled:opacity-50"
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>
                                <span className="px-3 py-1">Page {currentPage} of {totalPages}</span>
                                <button
                                    className="px-3 py-1 mx-1 border rounded disabled:opacity-50"
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LedgerCompDue;
